import { memo, useEffect, useState } from "react";
import Select from "react-select";
import { useGetUsersQuery } from "../../../features/ManageUsers/manageUsersSlice";
import toast from "react-hot-toast";
import { useDebounce } from "../../../hooks/useDebounce";

const UserMultiSelect = ({
  label,
  values = [],
  onChange,
  isDisabled = false,
  placeholder = "Select",
}) => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState([]);
  const debounceSearch = useDebounce(query);
  const [selectedOptions, setSelectedOptions] = useState(values || []);

  const {
    data: usersData,
    error: usersError,
    isFetching,
  } = useGetUsersQuery({
    email: "",
    query: "",
    orderBy: "name",
    orderByType: "Asc",
    name: debounceSearch,
    page,
    pageSize,
  });

  useEffect(() => {
    if (usersData) {
      const users = usersData?.ids.map((id) => usersData?.entities[id]);

      const newOptions = users?.map((user) => ({
        label: `${user.name} (${user?.email})`,
        value: user.id,
      }));

      setOptions((prevOptions) => {
        const seen = new Set();
        const mergedOptions = [...prevOptions, ...newOptions].filter((opt) => {
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });
        return mergedOptions;
      });

      setHasMore(users?.length >= pageSize);
    }

    if (usersError) {
      toast.error(usersError?.data?.Message || "Failed to load");
    }
  }, [usersData, usersError, pageSize]);

  const handleMenuScrollToBottom = () => {
    if (hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSearch = (inputValue) => {
    setQuery(inputValue);
    setPage(1);
    setOptions([]);
  };

  const handleChange = (selected) => {
    setSelectedOptions(selected);
    setQuery("");
    if (!selected?.length) {
      handleSearch("");
    }
  };

  return (
    <>
      {label && <label className="custom-label">{label}</label>}
      <Select
        className="paginated-users-multiple-selector"
        classNamePrefix="custom-select"
        options={options}
        defaultValue={selectedOptions}
        isMulti
        isDisabled={isDisabled}
        isClearable={true}
        isLoading={isFetching}
        onChange={handleChange}
        onBlur={() => onChange(selectedOptions)}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onInputChange={(inputValue, { action }) => {
          if (action === "input-change") {
            handleSearch(inputValue);
          }
        }}
        placeholder={placeholder}
      />
    </>
  );
};

export default memo(UserMultiSelect);
